import { 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User,
    sendPasswordResetEmail,
    updateProfile,
    updatePassword,
    EmailAuthProvider,
    reauthenticateWithCredential,
    ActionCodeSettings,
    deleteUser,
    Auth
} from 'firebase/auth';
import { auth as firebaseAuth } from '../firebase/config';
import { db } from '../firebase/config';
import { 
    collection, 
    query, 
    where, 
    getDocs,
    setDoc,
    getDoc,
    doc,
    runTransaction,
    updateDoc,
    Timestamp,
    writeBatch,
    addDoc,
    orderBy,
    deleteDoc,
    deleteField,
    increment,
    serverTimestamp,
    FieldValue,
    limit,
    startAt
} from 'firebase/firestore';
import { errorHandler } from '../utils/errorHandler';

// Ensure auth is initialized
let auth: Auth;
try {
    auth = firebaseAuth;
} catch (error) {
    throw new Error('Firebase auth initialization failed');
}

interface UserCredentials {
    email: string;
    password: string;
}

interface RegisterData {
    email: string;
    password: string;
    name: string;
    phone: string;
}

interface UserRegistration extends UserCredentials {
    displayName: string;
    phone: string;
}

interface UserProfile {
    id: string;
    name: string;
    email: string;
    phone: string;
    points: number;
    isActive: boolean;
    createdAt: Date;
    updatedAt?: Date;
}

interface PointsHistoryItem {
    id: string;
    points: number;
    reason: string;
    createdAt: string | null;
    referenceId?: string;
}

interface GetPointsHistoryResponse {
    items: PointsHistoryItem[];
    totalCount: number;
    lastDoc: any;
}

const USERS_COLLECTION = 'users';
const ADMINS_COLLECTION = 'admins';
const PHONE_NUMBERS_COLLECTION = 'phoneNumbers';
const ADMIN_EMAIL = 'admin@therosetarot.com';

// Firebase hata kodlarını Türkçe mesajlara çeviren yardımcı fonksiyon
const getErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    
    const errorCode = error.code || error.message;
    const errorMap: { [key: string]: string } = {
        'auth/invalid-credential': 'E-posta veya şifre hatalı.',
        'auth/user-not-found': 'Bu e-posta adresiyle kayıtlı bir hesap bulunamadı.',
        'auth/wrong-password': 'Şifre hatalı.',
        'auth/email-already-in-use': 'Bu e-posta adresi zaten kullanımda.',
        'auth/weak-password': 'Şifre en az 6 karakter olmalıdır.',
        'auth/invalid-email': 'Geçersiz e-posta adresi.',
        'auth/user-disabled': 'Bu hesap devre dışı bırakılmış.',
        'auth/too-many-requests': 'Çok fazla başarısız giriş denemesi. Lütfen bir süre bekleyin.',
        'auth/network-request-failed': 'İnternet bağlantınızı kontrol edin.',
        'auth/popup-closed-by-user': 'Oturum açma penceresi kapatıldı.',
        'auth/operation-not-allowed': 'Bu oturum açma yöntemi devre dışı.',
        'auth/requires-recent-login': 'Bu işlem için yeniden giriş yapmanız gerekiyor.',
        'unauthorized': 'Bu işlem için yetkiniz yok.',
        'account-disabled': 'Hesabınız devre dışı bırakılmış. Lütfen yönetici ile iletişime geçin.',
        'wrong-password': 'Mevcut şifreniz hatalı.',
        'email-already-in-use': 'Bu e-posta adresi başka bir hesap tarafından kullanılıyor.'
    };

    return errorMap[errorCode] || 'Bir hata oluştu. Lütfen tekrar deneyin.';
};

// ActionCodeSettings yapılandırması
const actionCodeSettings: ActionCodeSettings = {
    url: process.env.NODE_ENV === 'production' 
        ? 'https://therosetarot.com/login'
        : 'http://localhost:3000/login',
    handleCodeInApp: true
};

export const authService = {
    // Admin kontrolü
    isAdmin: async (user: User): Promise<boolean> => {
        try {
            if (!user) return false;
            const adminRef = doc(db, 'admins', user.uid);
            const adminDoc = await getDoc(adminRef);
            return adminDoc.exists();
        } catch (error) {
            throw error;
        }
    },

    // Giriş yap
    login: async (credentials: UserCredentials): Promise<void> => {
        try {
            await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
        } catch (error: any) {
            // Tüm giriş hatalarını genel bir mesaja çevir
            throw new Error('Hatalı giriş. Lütfen bilgilerinizi kontrol edip tekrar deneyin.');
        }
    },

    // Kayıt ol
    register: async (data: RegisterData): Promise<void> => {
        try {
            // Kullanıcı oluştur
            const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
            const user = userCredential.user;

            // Profil bilgilerini güncelle
            await updateProfile(user, {
                displayName: data.name
            });

            // Firestore'a kullanıcı bilgilerini kaydet
            await createUserProfile(user, data);
        } catch (error) {
            throw error;
        }
    },

    // Kullanıcı profilini getir
    getUserProfile: async (userId: string): Promise<UserProfile | null> => {
        try {
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            
            if (!userDoc.exists()) {
                return null;
            }

            const data = userDoc.data();
            
            // Timestamp dönüşümlerini güvenli bir şekilde yap
            let createdAt: Date;
            let updatedAt: Date | undefined;

            try {
                createdAt = data.createdAt?.toDate() || new Date();
            } catch (e) {
                createdAt = new Date();
            }

            try {
                updatedAt = data.updatedAt?.toDate();
            } catch (e) {
                updatedAt = undefined;
            }

            // Veri dönüşümlerini güvenli bir şekilde yap
            return {
                id: userId,
                name: data.name || '',
                email: data.email || '',
                phone: data.phone || '',
                points: typeof data.points === 'number' ? data.points : 0,
                isActive: typeof data.isActive === 'boolean' ? data.isActive : true,
                createdAt,
                updatedAt
            };
        } catch (error) {
            console.error('Error in getUserProfile:', error);
            throw new Error('Kullanıcı profili yüklenirken bir hata oluştu');
        }
    },

    // Şifre sıfırlama e-postası gönder
    sendPasswordResetEmail: async (email: string, actionCodeSettings?: ActionCodeSettings): Promise<void> => {
        try {
            await sendPasswordResetEmail(auth, email, actionCodeSettings);
        } catch (error) {
            throw error;
        }
    },

    // Çıkış yap
    logout: async (): Promise<void> => {
        try {
            await signOut(auth);
        } catch (error) {
            throw error;
        }
    },

    // Kullanıcı oturum durumu değişikliğini dinle
    onAuthStateChange: (callback: (user: User | null) => void) => {
        return onAuthStateChanged(auth, callback);
    },

    // Mevcut kullanıcıyı getir
    getCurrentUser: (): User | null => {
        return auth.currentUser;
    },

    // Şifre değiştir
    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        try {
            const user = auth.currentUser;
            if (!user || !user.email) {
                throw new Error('Kullanıcı oturumu bulunamadı');
            }

            // Mevcut şifreyi doğrula
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Yeni şifreyi ayarla
            await updatePassword(user, newPassword);
        } catch (error) {
            throw error;
        }
    },

    // Tüm kullanıcıları getir
    getAllUsers: async () => {
        try {
            const usersRef = collection(db, 'users');
            const querySnapshot = await getDocs(usersRef);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            throw error;
        }
    },

    // Kullanıcı sayısını getir
    getUserCount: async (): Promise<number> => {
        try {
            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);
            return snapshot.size;
        } catch (error) {
            throw error;
        }
    },

    // Kullanıcı durumunu güncelle
    updateUserStatus: async (userId: string, isActive: boolean): Promise<void> => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                isActive,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            throw error;
        }
    },

    // Puan ekle
    addPoints: async (userId: string, points: number): Promise<void> => {
        try {
            errorHandler.log('Starting addPoints operation:', { userId, points });
            
            // Önce mevcut puanı kontrol et
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            
            if (!userDoc.exists()) {
                errorHandler.log('User document not found');
                throw new Error('Kullanıcı bulunamadı');
            }
            
            const currentPoints = userDoc.data().points || 0;
            errorHandler.log('Current points:', currentPoints);
            
            // Transaction kullanarak puanları güncelle ve geçmişi kaydet
            await runTransaction(db, async (transaction) => {
                const newPoints = currentPoints + points;
                
                // Update user points
                transaction.update(userRef, {
                    points: newPoints,
                    updatedAt: serverTimestamp()
                });
                
                // Add points history entry
                const historyRef = collection(db, 'pointsHistory');
                const newHistoryRef = doc(historyRef); // Create a new document reference
                transaction.set(newHistoryRef, {
                    userId,
                    points,
                    reason: 'Puan ekleme',
                    createdAt: serverTimestamp()
                });
                
                errorHandler.log('Points will be updated to:', newPoints);
            });
            
            errorHandler.log('Points updated successfully');
        } catch (error) {
            errorHandler.handleError(error, 'Error in addPoints');
            throw error;
        }
    },

    // Puan geçmişi ekle
    addPointsHistory: async (userId: string, points: number, reason: string, referenceId?: string): Promise<void> => {
        try {
            errorHandler.log('Adding points history:', { userId, points, reason, referenceId });
            const historyRef = collection(db, 'pointsHistory');
            await addDoc(historyRef, {
                userId,
                points,
                reason,
                referenceId,
                createdAt: serverTimestamp()
            });
            errorHandler.log('Points history added successfully');
        } catch (error) {
            errorHandler.handleError(error, 'Error adding points history');
            throw new Error('Puan geçmişi eklenirken bir hata oluştu');
        }
    },

    // Puan geçmişini getir
    getPointsHistory: async (userId: string, pageSize: number = 5, lastDoc?: any): Promise<GetPointsHistoryResponse> => {
        try {
            const historyRef = collection(db, 'pointsHistory');
            let baseQuery = query(
                historyRef,
                where('userId', '==', userId),
                orderBy('createdAt', 'desc')
            );

            // Get total count first
            const totalCountSnapshot = await getDocs(baseQuery);
            const totalCount = totalCountSnapshot.size;

            // Add pagination to query
            if (pageSize) {
                baseQuery = query(baseQuery, limit(pageSize));
                if (lastDoc) {
                    baseQuery = query(baseQuery, startAt(lastDoc));
                }
            }
            
            const querySnapshot = await getDocs(baseQuery);
            
            const items = querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    points: data.points || 0,
                    reason: data.reason || '',
                    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : null,
                    referenceId: data.referenceId
                };
            });

            return {
                items,
                totalCount,
                lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1]
            };
        } catch (error) {
            throw error;
        }
    },

    // Admin ekle
    addAdmin: async (userId: string): Promise<void> => {
        try {
            const adminRef = doc(db, 'admins', userId);
            await setDoc(adminRef, {
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isActive: true
            });
        } catch (error) {
            throw error;
        }
    },

    // Adminleri getir
    getAdmins: async (): Promise<{
        id: string;
        email: string;
        name: string;
        isActive: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp | null;
    }[]> => {
        try {
            const adminsRef = collection(db, 'admins');
            const querySnapshot = await getDocs(adminsRef);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                email: doc.data().email || '',
                name: doc.data().name || '',
                isActive: doc.data().isActive ?? true,
                createdAt: doc.data().createdAt || serverTimestamp(),
                updatedAt: doc.data().updatedAt || null
            }));
        } catch (error) {
            throw error;
        }
    },

    // Admin durumunu güncelle
    updateAdminStatus: async (adminId: string, isActive: boolean): Promise<void> => {
        try {
            const adminRef = doc(db, 'admins', adminId);
            await updateDoc(adminRef, {
                isActive,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            throw error;
        }
    },

    // Puan ayarla
    setPoints: async (userId: string, points: number): Promise<void> => {
        try {
            const userRef = doc(db, 'users', userId);
            
            // Get current points first
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                throw new Error('Kullanıcı bulunamadı');
            }
            const currentPoints = userDoc.data().points || 0;
            const pointsDiff = points - currentPoints;

            // Use transaction to update points and add history atomically
            await runTransaction(db, async (transaction) => {
                // Update user points
                transaction.update(userRef, {
                    points,
                    updatedAt: serverTimestamp()
                });

                // Add points history entry
                const historyRef = collection(db, 'pointsHistory');
                const newHistoryRef = doc(historyRef); // Create a new document reference
                transaction.set(newHistoryRef, {
                    userId,
                    points: pointsDiff,
                    reason: 'Puan düzenleme',
                    createdAt: serverTimestamp()
                });
            });

            errorHandler.log('Points updated successfully:', { userId, points, pointsDiff });
        } catch (error) {
            errorHandler.handleError(error, 'Error in setPoints');
            throw error;
        }
    },

    // Kullanıcı sil
    deleteUser: async (userId: string): Promise<void> => {
        try {
            const userRef = doc(db, 'users', userId);
            await deleteDoc(userRef);
        } catch (error) {
            throw error;
        }
    },

    // Kullanıcı profilini güncelle
    updateUserProfile: async (userId: string, currentPassword: string, data: { name: string; phone: string }): Promise<void> => {
        try {
            const user = auth.currentUser;
            if (!user || !user.email) {
                throw new Error('Kullanıcı oturumu bulunamadı');
            }

            // Mevcut şifreyi doğrula
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Firebase Auth displayName güncelle
            await updateProfile(user, {
                displayName: data.name
            });

            // Firestore'daki kullanıcı bilgilerini güncelle
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                name: data.name,
                phone: data.phone,
                updatedAt: serverTimestamp()
            });
        } catch (error: any) {
            if (error.code === 'auth/wrong-password') {
                throw new Error('wrong-password');
            }
            throw error;
        }
    }
};

const createUserProfile = async (user: User, registerData: RegisterData): Promise<void> => {
    try {
        // Create user document with user's UID
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
            id: user.uid,  // Store UID as ID
            name: registerData.name,
            email: registerData.email,
            phone: registerData.phone,
            points: 0,
            isActive: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error creating user profile:', error);
        throw new Error('Kullanıcı profili oluşturulurken bir hata oluştu');
    }
}; 